// ICD-10 for a selected SNOMED concept.
//
// Primary path: POST snomed/map_icd/ with the concept id. One hit is applied
// automatically; several become a dropdown. If the map is down or empty, fall
// back to the local ICD-10 catalog so the form is still completable.

import React, { useEffect, useRef, useState } from "react";
import { MenuItem } from "@mui/material";
import IpdTextField from "../../ipd/utils/IpdTextField";
import { C } from "../constants";
import { mapSnomedToIcd10 } from "../snomed-api";
import Icd10SearchField from "./Icd10SearchField";

export default function Icd10MappedField({ snomedCode, code, display, onChange, compact }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [hint, setHint] = useState("");
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!snomedCode) {
      setOptions([]);
      setHint("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setHint("");
    setOptions([]);

    mapSnomedToIcd10(snomedCode)
      .then((rows) => {
        if (cancelled) return;
        setOptions(rows);
        if (rows.length === 1) {
          onChangeRef.current(rows[0]);
          setHint("From the SNOMED → ICD-10 map");
        } else if (rows.length > 1) {
          setHint("This concept maps to more than one ICD-10 code — pick one.");
        } else {
          setHint("No ICD-10 map for this concept — search below.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setOptions([]);
        setHint(err?.message || "ICD-10 map unavailable — search below.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [snomedCode]);

  if (!snomedCode && !code) return null;

  const extraHint = compact ? null : hint ? (
    <div style={{ fontSize: "11px", color: options.length ? C.textSub : C.warning, marginTop: "5px" }}>{hint}</div>
  ) : null;

  if (loading) {
    return (
      <IpdTextField
        fullWidth
        disabled
        value=""
        placeholder={snomedCode ? `Mapping SNOMED ${snomedCode}…` : "Looking up ICD-10…"}
      />
    );
  }

  if (options.length > 1) {
    return (
      <div>
        <IpdTextField
          select
          fullWidth
          value={code || ""}
          onChange={(e) => {
            const picked = options.find((o) => o.code === e.target.value);
            if (picked) onChange(picked);
          }}
        >
          <MenuItem value="">Select ICD-10…</MenuItem>
          {options.map((o) => (
            <MenuItem key={o.code} value={o.code}>
              {o.display} ({o.code})
            </MenuItem>
          ))}
        </IpdTextField>
        {extraHint}
      </div>
    );
  }

  if (options.length === 1) {
    const shownCode = code || options[0].code;
    const shownDisplay = display || options[0].display;
    return (
      <div>
        <IpdTextField fullWidth disabled value={`${shownDisplay} (${shownCode})`} />
        {extraHint}
      </div>
    );
  }

  return (
    <div>
      {compact ? null : extraHint}
      <Icd10SearchField
        term={display}
        code={code}
        placeholder="e.g. unstable angina or I20.0"
        onTermChange={(value) => onChange({ code: "", display: value })}
        onSelect={(row) => onChange({ code: row.code, display: row.display })}
      />
    </div>
  );
}
